import {isPlatformBrowser} from '@angular/common';
import {EventEmitter, inject, Injectable, PLATFORM_ID} from '@angular/core';
import {APIService} from "./api.service";
import {IChallengeListItem, IState, IStateUser, Permission, WelcomeStatus} from "./api.types";
import {ActivatedRoute, ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot} from "@angular/router";
import {of} from "rxjs";

export const authAdminResolver = () => {
  const auth = inject(AuthService);
  return of(auth.perm('ap-access') || inject(Router).navigateByUrl('/'));
}

export const authUserGuard = () => {
  const isLogged = inject(AuthService).isLogged;
  return of(isLogged || inject(Router).navigateByUrl('/login'));
}

// Redirect logged-in user to home page
export const guestGuard: CanActivateFn = () => {
  return !inject(AuthService).isLogged || inject(Router).parseUrl('/');
}

// Check for blocked user
export const blockedGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  if (auth.user.blocked) {
    return router.navigateByUrl('/blocked');
  }
  return true;
}

// Check for new member welcome status
export const newMemberGuard = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const api = inject(APIService);

  // Check welcome status
  if (auth.user.welcome_status === 'welcome') {

    // Allow welcome page
    if (state.url === '/welcome') {
      return true;
    }

    // Check for activation challenge
    let challenge: IChallengeListItem|undefined = undefined;
    try {
      challenge = await api.getActivationChallenge();
    } catch  {}

    // Allow challenge page
    if (challenge?.id && state.url.startsWith(`/challenge/${challenge.id}`)) {
      return true;
    }

     return router.navigateByUrl('/welcome');
  }

  // Check updating status
  if (auth.user.welcome_status === 'updating') {
    // Allow challenge page
    if (state.url.startsWith(`/challenge/`) || state.url === '/') {
      return true;
    }
    return router.navigateByUrl('/');
  }

  return true;
}

export const canActivatePermission = (perm: Permission) => {
  return (() => {
    const auth = inject(AuthService);
    return of(auth.perm(perm) || inject(Router).navigateByUrl('/'));
  }) as CanActivateFn;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private api = inject(APIService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private state?: IState;
  private emptyUser: IStateUser = {
    id: -1,
    name: '',
    points: 0,
    cached_total_points: 0,
    total_points: 0,
    ranking: 0,
    roles: [],
    permissions: [],
    blocked: false,
    deletion_request_sent: false,
    unblock_message_sent: false,
    welcome_status: 'finished',
    daily_reward: {
      streak_day_index: 0,
      points: [0, 0, 0, 0, 0],
      can_claim: false,
      countdown: 0
    }
  };
  private roleMap: Map<string, boolean> = new Map();
  private permissions: Map<Permission, boolean> = new Map();
  onCoinsChanged = new EventEmitter<number>();
  onLogout = new EventEmitter<void>();
  onUserStatusChanged = new EventEmitter<WelcomeStatus>();

  perm(permission: Permission): boolean {
    return !!this.permissions.get(permission);
  }

  get isLogged() {
    return !!this.state?.auth;
  }

  get user() {
    return this.state?.user || this.emptyUser;
  }

  get suggestedChallengeId() {
    return this.state?.suggestedChallengeId;
  }

  get inviteMemberChallengeId() {
    return this.state?.inviteMemberChallengeId;
  }

  async initializeUserState() {
    return this.api.getState()
      .then(state => {
        this.state = state;
        if (state.user?.daily_reward) {
          state.user.daily_reward.deadline = Date.now() + (state.user.daily_reward.countdown ?? 0) * 1000;
        }
        this.buildRoleMap();
        this.buildPermissionsMap();
      });
  }

  private buildRoleMap() {
    this.roleMap = new Map();
    if (!this.state?.user) {
      return;
    }
    this.state.user.roles.forEach(r => {
      this.roleMap.set(r, true);
    });
  }

  async login(doRegistration = false, redirectUrl = '') {
    const resp = await this.api.getOAuthUrl({
      url: redirectUrl || location.href,
      registration: doRegistration ? 1 : 0
    });

    location.href = resp.url;
  }

  /** Starts a fresh OAuth flow after a failed callback. */
  async retryLogin(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      const resp = await this.api.getOAuthUrl({
        url: `${location.origin}/`,
        registration: 0
      });

      if (typeof resp?.url !== 'string' || !resp.url) {
        throw new Error('OAuth authorization URL is missing');
      }

      location.assign(resp.url);
      return true;
    } catch {
      console.error('Unable to start OAuth sign-in');
      return false;
    }
  }

  register(redirectUrl = '') {
    void this.login(true, redirectUrl);
  }

  async logout() {
    try {
      await this.api.doLogout()
      this.roleMap = new Map();
      this.permissions = new Map();
      if (this.state) {
        this.state.auth = false;
        this.state.user = this.emptyUser;
      }
      this.onLogout.emit();
      void this.router.navigateByUrl('/login');
    } catch (e) {
      console.error(e);
    }
  }

  private buildPermissionsMap() {
    this.permissions = new Map();
    if (!this.state?.user) {
      return;
    }
    this.state.user.permissions.forEach(p => {
      this.permissions.set(p, true);
    })
  }
}
